import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from './auth/services/auth.service';
import { ApplicationFrameComponent, FooterLink, SidebarItem } from '@siemens/component-lib';
import { HeaderMenuComponent } from './shared/components/header-menu/header-menu.component';
import { Title } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppComponentOperations } from './app-component.operations';
import { Subject, takeUntil, tap } from 'rxjs';
import { select } from '@ngxs/store';
import { UserManagementState } from './user-management/state/user-management.state';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [RouterOutlet, ApplicationFrameComponent, HeaderMenuComponent, TranslatePipe]
})
export class AppComponent implements OnInit, OnDestroy {
    protected router = inject(Router);
    protected authService = inject(AuthService);
    private translateService = inject(TranslateService);
    private pageTitleService = inject(Title);

    protected sidebarItems = signal<SidebarItem[]>([]);
    protected unauthorizedSidebarItems = signal<SidebarItem[]>([]);
    protected bottomSidebarItems = signal<SidebarItem[]>([]);
    protected footerLinks = signal<FooterLink[]>([]);
    protected isUserLoading = select(UserManagementState.isMeLoading);
    private destroy$: Subject<boolean> = new Subject<boolean>();

    ngOnInit() {
        this.subscribeToLanguageChanges();
        this.updateTranslations();
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected isRouterPathUnauthorized(): boolean {
        return this.router.url === '/unauthorized';
    }

    private updateApplicationFrameTranslations(): void {
        const { sidebarItems, footerLinks, bottomSidebarItems, unauthorizedSidebarItems } =
            AppComponentOperations.getInitializedApplicationFrameTranslations(this.translateService);
        this.sidebarItems.set(sidebarItems);
        this.unauthorizedSidebarItems.set(unauthorizedSidebarItems);
        this.footerLinks.set(footerLinks);
        this.bottomSidebarItems.set(bottomSidebarItems);
    }

    private updatePageTitleTranslation(): void {
        this.pageTitleService.setTitle(this.translateService.instant('page-titles.home'));
    }

    private subscribeToLanguageChanges(): void {
        this.translateService.onLangChange
            .pipe(
                tap(() => this.updateTranslations()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private updateDocumentLang(): void {
        const currentLang = this.translateService.currentLang() ?? 'en';
        document.documentElement.lang = currentLang;
    }

    private updateTranslations(): void {
        this.updateApplicationFrameTranslations();
        this.updatePageTitleTranslation();
        this.updateDocumentLang();
    }
}
